import { useMemo } from 'react';
import type { Point, Projet } from '../../types/map';
import { MIN_SEARCH_CHARS, type FilterOptions } from './searchTypes';

/** Marqueur "tous fonds de carte" dans `Projet.baseMapIds`. */
const WILDCARD = '*';

/** Minuscule + suppression des diacritiques (déterministe, sans locale). */
export function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Fuzzy match par sous-séquence : tous les caractères de `query` apparaissent
 * dans `text`, dans l'ordre (pas forcément contigus). `query` et `text` doivent
 * être déjà normalisés (cf. {@link normalize}). Fonction PURE.
 */
export function fuzzyMatch(query: string, text: string): boolean {
  if (!query) return true;
  let qi = 0;
  for (let ti = 0; ti < text.length && qi < query.length; ti++) {
    if (text[ti] === query[qi]) qi++;
  }
  return qi === query.length;
}

/** Un emplacement s'affiche sur le fond `baseMapId` s'il y est rattaché (`baseMapIds` effectif fourni par l'API). */
function pointOnBaseMap(point: Point, baseMapId: string): boolean {
  const ids = point.baseMapIds;
  if (!ids) return true;
  return ids.includes(WILDCARD) || ids.includes(baseMapId);
}

/**
 * Filtrage CÔTÉ CLIENT des résultats (SPEC §2.3, §2.7, §3.6), opérant sur les
 * PROJETS (unité de résultat) plutôt que sur leurs emplacements individuels.
 *
 * Combinaison ET :
 *  - publié : `projet.visible !== false` (défensif ; l'API filtre déjà) ;
 *  - rattaché au fond de carte sélectionné : `projet.baseMapIds` inclut
 *    `baseMapId` (ou le wildcard `'*'`) — ignoré si `baseMapId` est indéfini ;
 *  - catégorie cochée : `projet.categorieId` ∈ `checkedCategorieIds` ;
 *  - mots-clés : appliqué seulement si `trim().length >= MIN_SEARCH_CHARS`,
 *    fuzzy match sur `titre` (sinon `nom`).
 *
 * Le viewport N'INTERVIENT PAS (SPEC §2.3). Un projet sans emplacement (0 point)
 * passe par le même filtre ; son picto est simplement masqué à l'affichage.
 * Les emplacements des projets retournés sont restreints à ceux rattachés au
 * fond de carte sélectionné (un projet peut n'en avoir aucun sur ce fond).
 *
 * Fonction PURE et déterministe : aucun effet de bord, aucun appel réseau.
 */
export function filterResults(projets: Projet[], opts: FilterOptions): Projet[] {
  const { baseMapId, checkedCategorieIds, keywords } = opts;

  const trimmed = keywords.trim();
  const applyText = trimmed.length >= MIN_SEARCH_CHARS;
  const normalizedQuery = applyText ? normalize(trimmed) : '';

  return projets
    .filter((p) => {
      // Publié (≠ « marqueur visible » dans le viewport, SPEC §2.3).
      if (p.visible === false) return false;

      // Fond de carte (campus).
      if (baseMapId) {
        const ids = p.baseMapIds;
        const matchesBaseMap =
          !!ids && ids.length > 0 && (ids.includes(WILDCARD) || ids.includes(baseMapId));
        if (!matchesBaseMap) return false;
      }

      // Catégorie (une seule par projet, SPEC §2.3).
      if (p.categorieId == null || !checkedCategorieIds.has(p.categorieId)) return false;

      // Mots-clés (fuzzy), seulement au-delà du seuil.
      if (applyText) {
        const haystack = normalize(p.titre || p.nom || '');
        if (!fuzzyMatch(normalizedQuery, haystack)) return false;
      }

      return true;
    })
    .map((p) =>
      baseMapId ? { ...p, points: (p.points || []).filter((pt) => pointOnBaseMap(pt, baseMapId)) } : p
    );
}

/** Hook mémoïsé autour de {@link filterResults}. */
export function useResultsFilter(projets: Projet[], opts: FilterOptions): Projet[] {
  const { baseMapId, checkedCategorieIds, keywords } = opts;
  return useMemo(
    () => filterResults(projets, { baseMapId, checkedCategorieIds, keywords }),
    [projets, baseMapId, checkedCategorieIds, keywords]
  );
}
